import { useEffect, useState } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [shop, setShop] = useState(null);
  const [period, setPeriod] = useState("today");
  const [message, setMessage] = useState("");

  const token = localStorage.getItem("token")?.trim();

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const headers = {
          Authorization: `Bearer ${token}`,
        };

        const [dashboardRes, shopRes] = await Promise.all([
          axios.get(`${API_URL}/dashboard/summary?period=${period}`, {
            headers,
          }),
          axios.get(`${API_URL}/shop`, {
            headers,
          }),
        ]);

        setSummary(dashboardRes.data);
        setShop(shopRes.data);
        setMessage("");
      } catch (error) {
        setMessage(
          error.response?.data?.message || "Could not load dashboard."
        );
      }
    };

    fetchDashboard();
  }, [token, period]);

  if (message) return <p style={styles.message}>{message}</p>;
  if (!summary || !shop) {
    return <p style={styles.message}>Loading dashboard...</p>;
  }

  return (
    <div style={styles.page}>
      {/* SHOP HEADER */}
      <div style={styles.shopHeader}>
        <div>
          <h1 style={styles.shopName}>{shop.shopName || "Dashboard"}</h1>

          {shop.address && <p style={styles.shopText}>{shop.address}</p>}

          <p style={styles.shopText}>
            {shop.phone && <>Tel: {shop.phone}</>}
            {shop.phone && shop.email && " | "}
            {shop.email && <>{shop.email}</>}
          </p>

          {shop.website && <p style={styles.shopText}>{shop.website}</p>}
        </div>

        <a href="/shop-settings" style={styles.settingsLink}>
          Edit Shop Details
        </a>
      </div>

      {/* PERIOD FILTERS */}
      <div style={styles.periodBox}>
        <button
          onClick={() => setPeriod("today")}
          style={{
            ...styles.periodButton,
            ...(period === "today" ? styles.activePeriodButton : {}),
          }}
        >
          Today
        </button>

        <button
          onClick={() => setPeriod("week")}
          style={{
            ...styles.periodButton,
            ...(period === "week" ? styles.activePeriodButton : {}),
          }}
        >
          Last 7 Days
        </button>

        <button
          onClick={() => setPeriod("month")}
          style={{
            ...styles.periodButton,
            ...(period === "month" ? styles.activePeriodButton : {}),
          }}
        >
          Last 30 Days
        </button>

        <button
          onClick={() => setPeriod("all")}
          style={{
            ...styles.periodButton,
            ...(period === "all" ? styles.activePeriodButton : {}),
          }}
        >
          All Time
        </button>
      </div>

      {/* TOP ANALYTICS CARDS */}
      <div style={styles.cards}>
        <div style={styles.card}>
          <p>Gross Revenue</p>
          <h2>£{Number(summary.grossRevenue || 0).toFixed(2)}</h2>
        </div>

        <div style={styles.card}>
          <p>Refunds</p>
          <h2>£{Number(summary.refundAmount || 0).toFixed(2)}</h2>
        </div>

        <div style={styles.card}>
          <p>Net Revenue</p>
          <h2>£{Number(summary.netRevenue || 0).toFixed(2)}</h2>
        </div>

        <div style={styles.card}>
          <p>Net Profit</p>
          <h2>£{Number(summary.netProfit || 0).toFixed(2)}</h2>
        </div>

        <div style={styles.card}>
          <p>Gross Profit</p>
          <h2>£{Number(summary.grossProfit || 0).toFixed(2)}</h2>
        </div>

        <div style={styles.card}>
          <p>Refunded Profit</p>
          <h2>£{Number(summary.refundProfit || 0).toFixed(2)}</h2>
        </div>

        <div style={styles.card}>
          <p>Sales Count</p>
          <h2>{summary.salesCount || 0}</h2>
        </div>

        <div style={styles.card}>
          <p>Average Sale</p>
          <h2>£{Number(summary.averageSale || 0).toFixed(2)}</h2>
        </div>

        <div style={styles.card}>
          <p>Cash Sales</p>
          <h2>£{Number(summary.cashTotal || 0).toFixed(2)}</h2>
        </div>

        <div style={styles.card}>
          <p>Card Sales</p>
          <h2>£{Number(summary.cardTotal || 0).toFixed(2)}</h2>
        </div>
      </div>

      {/* LOWER INSIGHT PANELS */}
      <div style={styles.grid}>
        <section style={styles.panel}>
          <h2>Best Selling Products</h2>

          {summary.bestSellingProducts.length === 0 ? (
            <p>No sales found for this period.</p>
          ) : (
            summary.bestSellingProducts.map((product) => (
              <div key={product.productId} style={styles.row}>
                <div>
                  <strong>{product.name}</strong>
                  <p>
                    Sold: {Number(product.quantitySold).toFixed(2)}{" "}
                    {product.unitLabel}
                  </p>
                </div>

                <div style={styles.rightText}>
                  <strong>£{Number(product.revenue).toFixed(2)}</strong>
                  <p>Profit: £{Number(product.profit).toFixed(2)}</p>
                </div>
              </div>
            ))
          )}
        </section>

        <section style={styles.panel}>
          <h2>Low Stock</h2>

          {summary.lowStockProducts.length === 0 ? (
            <p>No low stock items.</p>
          ) : (
            summary.lowStockProducts.map((product) => (
              <div key={product._id} style={styles.row}>
                <div>
                  <strong>{product.name}</strong>
                  <p>{product.category}</p>
                </div>

                <div style={styles.rightText}>
                  <strong>
                    {product.stockQuantity} {product.unitLabel}
                  </strong>
                  <p>Alert: {product.lowStockAlert}</p>
                </div>
              </div>
            ))
          )}
        </section>
        <section style={styles.panel}>
  <h2>Sales by Terminal</h2>

  {!summary.salesByTerminal || summary.salesByTerminal.length === 0 ? (
    <p>No terminal sales found.</p>
  ) : (
    summary.salesByTerminal.map((terminal) => (
      <div key={terminal._id || "Unknown Terminal"} style={styles.row}>
        <div>
          <strong>{terminal._id || "Unknown Terminal"}</strong>
          <p>{terminal.salesCount} sales</p>
        </div>

        <div style={styles.rightText}>
          <strong>£{Number(terminal.totalSales || 0).toFixed(2)}</strong>
        </div>
      </div>
    ))
  )}
</section>

<section style={styles.panel}>
  <h2>Sales by Cashier</h2>

  {!summary.salesByCashier || summary.salesByCashier.length === 0 ? (
    <p>No cashier sales found.</p>
  ) : (
    summary.salesByCashier.map((cashier) => (
      <div key={cashier._id || "Unknown Cashier"} style={styles.row}>
        <div>
          <strong>{cashier._id || "Unknown Cashier"}</strong>
          <p>{cashier.salesCount} sales</p>
        </div>

        <div style={styles.rightText}>
          <strong>£{Number(cashier.totalSales || 0).toFixed(2)}</strong>
        </div>
      </div>
    ))
  )}
</section>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    padding: "24px",
    background: "#f4f6f8",
    fontFamily: "Arial, sans-serif",
  },
  message: {
    padding: "24px",
    fontFamily: "Arial, sans-serif",
  },
  shopHeader: {
    background: "white",
    padding: "22px",
    borderRadius: "14px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    marginBottom: "18px",
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    alignItems: "flex-start",
  },
  shopName: {
    margin: "0 0 8px",
  },
  shopText: {
    margin: "4px 0",
    color: "#555",
  },
  settingsLink: {
    padding: "10px 14px",
    borderRadius: "8px",
    background: "#f4f6f8",
    color: "#111",
    textDecoration: "none",
    border: "1px solid #ddd",
  },
  periodBox: {
    display: "flex",
    gap: "10px",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
  periodButton: {
    padding: "12px 16px",
    borderRadius: "999px",
    border: "1px solid #ddd",
    background: "white",
    cursor: "pointer",
    fontSize: "15px",
  },
  activePeriodButton: {
    background: "#111827",
    color: "white",
    border: "1px solid #111827",
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "16px",
    marginBottom: "24px",
  },
  card: {
    background: "white",
    padding: "20px",
    borderRadius: "14px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
  },
  panel: {
    background: "white",
    padding: "20px",
    borderRadius: "14px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    padding: "14px 0",
    borderBottom: "1px solid #eee",
  },
  rightText: {
    textAlign: "right",
  },
};

export default Dashboard;